// client/src/components/company/CompanyDashboard.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { formatDuration, formatCurrencyWithSymbol, formatInterestRate } from '../utils/formatUtils';
import { useResponsive } from '../../hooks/useResponsive';
import { TrendingUp, Mail, CalendarClock, PieChart as PieChartIcon, Upload, Download, Receipt } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';

// Colori per prodotto (badge e importi), coerenti con il resto dell'app
const PRODUCT_STYLES = {
  allocation: { color: '#1a73e8', badge: { backgroundColor: '#cfe3fb', color: '#0d47a1' } },
  hot_money: { color: '#e65100', badge: { backgroundColor: '#f8dfb0', color: '#8a5300' } },
  vat_credit: { color: '#5a6b82', badge: { backgroundColor: '#eef1f6', color: '#5a6b82' } }
};

// Pie chart palettes per product: deposits keep the blue/gray scale, hot money uses warm orange/yellow tones
const PIE_COLORS = {
  allocation: ['#1a73e8', '#5c9ce6', '#2e7d32', '#78909c', '#f4a742', '#607d8b', '#8e44ad', '#95a5a6', '#3498db', '#7f8c8d'],
  hot_money: ['#e65100', '#ffb300', '#bf360c', '#ffd54f', '#fb8c00', '#8d6e63', '#f4511e', '#fdd835', '#ffa726', '#d84315']
};

// Shared tooltip style so all pie charts read as one system
const pieTooltipStyle = {
  borderRadius: '8px',
  border: '1px solid rgba(11,11,11,0.10)',
  boxShadow: '0 2px 8px rgba(11,11,11,0.12)',
  fontSize: '13px',
  padding: '8px 12px'
};
const DONUT_SIZE = 170;

const kpiLabelStyle = { fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: '#767e8e' };

const CompanyDashboard = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { isAuthenticated, token, initialized, user } = useAuth();
  // Passed to the detail pages so their back button returns here
  const location = useLocation();
  const fromDashboard = { from: location.pathname };

  // Stesse regole della sidebar: denaro caldo salvo non paganti espliciti, Crediti IVA solo paganti
  const showHotMoney = user?.organization?.isPaying !== false;
  const showVatCredit = user?.organization?.isPaying === true;

  const [activeRequests, setActiveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeRequestsFilter, setActiveRequestsFilter] = useState('all');
  const [maturitiesFilter, setMaturitiesFilter] = useState('all');

  // Riepilogo (scadenze, outstanding, totali): ogni card sceglie la propria valuta
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [cardCurrencies, setCardCurrencies] = useState({});

  useEffect(() => {
    const fetchActiveRequests = async () => {
      setLoading(true);
      setError('');

      try {
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }

        // Liquidity/hot money + VAT credit (only for companies that can access it)
        const [requestsRes, vatCreditRes] = await Promise.all([
          axios.get('/api/requests'),
          showVatCredit ? axios.get('/api/vat-credit-requests') : Promise.resolve({ data: { requests: [] } })
        ]);

        const active = requestsRes.data.requests.filter(request =>
          ['bidding', 'pending_allocation'].includes(request.status) &&
          (showHotMoney || request.requestType !== 'hot_money')
        );

        // Tagged so they render with their own columns/badge
        const activeVatCredit = (vatCreditRes.data.requests || [])
          .filter(request => ['bidding', 'pending_allocation'].includes(request.status))
          .map(request => ({ ...request, requestKind: 'vat_credit' }));

        setActiveRequests(
          [...active, ...activeVatCredit].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        );
      } catch (err) {
        console.error('Dashboard data fetch error:', err);
        setError(t('common.errors.fetchDashboard', {
          defaultValue: 'Failed to fetch dashboard data. Please try again.'
        }));
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && initialized) {
      fetchActiveRequests();
    }
  }, [isAuthenticated, token, initialized, t, showHotMoney, showVatCredit]);

  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }

        const response = await axios.get('/api/requests/dashboard/summary');
        setSummary(response.data);
      } catch (err) {
        console.error('Dashboard summary fetch error:', err);
      } finally {
        setSummaryLoading(false);
      }
    };

    if (isAuthenticated && initialized) {
      fetchSummary();
    }
  }, [isAuthenticated, token, initialized]);

  // Helper function to translate status
  const getStatusTranslation = (status) => {
    switch (status) {
      case 'bidding':
        return t('status.bidding');
      case 'pending_allocation':
        return t('status.pending');
      case 'completed':
        return t('status.completed');
      case 'cancelled':
        return t('status.cancelled');
      default:
        return status.replace('_', ' ');
    }
  };

  const getProductKey = (request) => {
    if (request.requestKind === 'vat_credit') return 'vat_credit';
    return request.requestType === 'hot_money' ? 'hot_money' : 'allocation';
  };

  const PRODUCT_LABELS = {
    allocation: t('requests.allocation'),
    hot_money: t('requests.hotMoney'),
    vat_credit: t('requests.vatCredit')
  };

  const renderTypeBadge = (productKey) => (
    <span className="status" style={PRODUCT_STYLES[productKey].badge}>
      {PRODUCT_LABELS[productKey]}
    </span>
  );

  // Filtri a pulsanti nell'intestazione delle card (solo per chi ha più di un prodotto)
  const renderFilterButtons = (options, value, onChange) => (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: 'auto' }}>
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          className={`btn btn-sm ${value === option.value ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  const allOption = { value: 'all', label: t('dashboard.filterAll') };
  const activeRequestsFilterOptions = [
    allOption,
    { value: 'allocation', label: t('sidebar.allocations') },
    ...(showHotMoney ? [{ value: 'hot_money', label: t('sidebar.hotMoney') }] : []),
    ...(showVatCredit ? [{ value: 'vat_credit', label: t('sidebar.vatCredit') }] : [])
  ];
  const maturitiesFilterOptions = activeRequestsFilterOptions.filter(o => o.value !== 'vat_credit');

  const filteredActiveRequests = activeRequestsFilter === 'all'
    ? activeRequests
    : activeRequests.filter(request => getProductKey(request) === activeRequestsFilter);

  const maturities = summary?.maturities || [];
  const filteredMaturities = maturitiesFilter === 'all'
    ? maturities
    : maturities.filter(m => m.requestType === maturitiesFilter);

  const getRequestLink = (request) => (request.requestKind === 'vat_credit'
    ? `/company/vat-credit/requests/${request.id}`
    : `/company/requests/${request.id}`);

  const formatDaysLeft = (daysLeft) => {
    if (daysLeft <= 0) return t('dashboard.today');
    if (daysLeft === 1) return t('dashboard.inOneDay');
    return t('dashboard.inDays', { n: daysLeft });
  };

  const formatRate = (rate) => (rate ? formatInterestRate(rate.interestRate, rate.rateType, rate.baseRate) : '-');

  // Mobile Card Component for Active Requests
  const MobileActiveRequestCard = ({ request }) => (
    <div className="mobile-record-card">
      <div className="mobile-card-header">
        <div className="mobile-card-title">
          #{request.requestId}
        </div>
        <div className="mobile-card-status" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span className={`status status-${request.status}`}>
            {getStatusTranslation(request.status)}
          </span>
        </div>
      </div>

      <div className="mobile-card-body">
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.dateCreated')}</span>
          <span className="mobile-card-value">{new Date(request.createdAt).toLocaleDateString()}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.amount')}</span>
          <span className="mobile-card-value">{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</span>
        </div>

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('bids.bidsReceived')}</span>
          <span className="mobile-card-value">{formatCurrencyWithSymbol(request.totalActiveBidAmount, request.currency || 'EUR')}</span>
        </div>

        {showHotMoney && (
          <div className="mobile-card-row">
            <span className="mobile-card-label">{t('requests.type')}</span>
            <span className="mobile-card-value">{renderTypeBadge(getProductKey(request))}</span>
          </div>
        )}

        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.maturity')}</span>
          <span className="mobile-card-value">
            {request.requestKind === 'vat_credit' ? '-' : formatDuration(request.duration, t)}
          </span>
        </div>
      </div>

      <div className="mobile-card-footer">
        <div className="mobile-card-date" />
        <div className="mobile-card-action">
          <Link to={getRequestLink(request)} state={fromDashboard} className="view-btn">
            {t('common.view')}
          </Link>
        </div>
      </div>
    </div>
  );

  // Mobile Card Component for Upcoming Maturities
  const MobileMaturityCard = ({ maturity }) => (
    <div className="mobile-record-card">
      <div className="mobile-card-header">
        <div className="mobile-card-title">
          {new Date(maturity.endDate).toLocaleDateString()}
        </div>
        <div className="mobile-card-status">
          {showHotMoney && renderTypeBadge(maturity.requestType)}
        </div>
      </div>

      <div className="mobile-card-body">
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('dashboard.daysLeft')}</span>
          <span className="mobile-card-value">{formatDaysLeft(maturity.daysLeft)}</span>
        </div>
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('dashboard.bank')}</span>
          <span className="mobile-card-value">{maturity.bankName}</span>
        </div>
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('requests.amount')}</span>
          <span className="mobile-card-value">{formatCurrencyWithSymbol(maturity.amount, maturity.currency)}</span>
        </div>
        <div className="mobile-card-row">
          <span className="mobile-card-label">{t('dashboard.rate')}</span>
          <span className="mobile-card-value">{formatRate(maturity)}</span>
        </div>
      </div>

      <div className="mobile-card-footer">
        <div className="mobile-card-date" />
        <div className="mobile-card-action">
          <Link to={`/company/requests/${maturity.requestMongoId}`} state={fromDashboard} className="view-btn">
            {t('dashboard.detail')}
          </Link>
        </div>
      </div>
    </div>
  );

  // Currencies with data for a card: EUR first, then alphabetical
  const sortCurrencies = (currencies) => [...currencies].sort((a, b) => {
    if (a === 'EUR') return -1;
    if (b === 'EUR') return 1;
    return a.localeCompare(b);
  });

  // Card currency: the one picked by the user, otherwise EUR (or the only currency with data)
  const resolveCardCurrency = (cardKey, currencies) => {
    const chosen = cardCurrencies[cardKey];
    if (chosen && currencies.includes(chosen)) return chosen;
    return currencies.length === 0 || currencies.includes('EUR') ? 'EUR' : currencies[0];
  };

  // Selector in the card title, only when the card has data in more than one currency
  const renderCardCurrencySelector = (cardKey, currencies, value) => (
    currencies.length > 1 && (
      <select
        aria-label={t('dashboard.currency')}
        value={value}
        onChange={(e) => setCardCurrencies(prev => ({ ...prev, [cardKey]: e.target.value }))}
        style={{ width: 'auto', marginLeft: 'auto', padding: '4px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', fontWeight: 500, cursor: 'pointer', backgroundColor: '#fff' }}
      >
        {currencies.map(cur => (
          <option key={cur} value={cur}>{cur}</option>
        ))}
      </select>
    )
  );

  const renderOutstandingCard = (productKey) => {
    const byCurrency = summary?.outstanding?.[productKey];
    if (!byCurrency) return null;
    const cardKey = `outstanding-${productKey}`;
    const currencies = sortCurrencies(Object.keys(byCurrency));
    const currency = resolveCardCurrency(cardKey, currencies);
    const data = byCurrency[currency];
    const { color } = PRODUCT_STYLES[productKey];
    const palette = PIE_COLORS[productKey];
    const bankColor = (index) => palette[index % palette.length];

    return (
      <div className="card" key={productKey} style={{ marginBottom: 0 }}>
        <div className="card-title">
          <PieChartIcon className="card-title-icon" size={20} style={{ color }} />
          <span style={{ color }}>
            {productKey === 'hot_money'
              ? t('dashboard.hotMoneyOutstanding')
              : t('dashboard.activeDeposits')}
          </span>
          {renderCardCurrencySelector(cardKey, currencies, currency)}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
          {/* Donut with the number of outstanding positions in the middle */}
          <div style={{ position: 'relative', width: DONUT_SIZE, height: DONUT_SIZE, flexShrink: 0, margin: isMobile ? '0 auto' : 0 }}>
            <PieChart width={DONUT_SIZE} height={DONUT_SIZE}>
              <Pie
                data={data.byBank}
                dataKey="value"
                nameKey="name"
                innerRadius={58}
                outerRadius={82}
                startAngle={90}
                endAngle={-270}
                paddingAngle={data.byBank.length > 1 ? 2 : 0}
                cornerRadius={3}
                stroke="none"
              >
                {data.byBank.map((entry, index) => (
                  <Cell key={`cell-${productKey}-${entry.name}`} fill={bankColor(index)} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => formatCurrencyWithSymbol(value, currency)}
                contentStyle={pieTooltipStyle}
                wrapperStyle={{ zIndex: 10 }}
              />
            </PieChart>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ fontSize: '26px', fontWeight: 700, color: '#1f2937', lineHeight: 1.1 }}>{data.count}</div>
              <div style={{ fontSize: '11px', color: '#767e8e', textAlign: 'center', maxWidth: '90px' }}>
                {t('dashboard.positionsOutstanding')}
              </div>
            </div>
          </div>

          {/* Right column: outstanding total, then legend with bank, amount and share */}
          <div style={{ flex: '1 1 220px', minWidth: 0 }}>
            <div style={{ fontSize: '26px', fontWeight: 'bold', color, lineHeight: 1.2, marginBottom: '6px' }}>
              {formatCurrencyWithSymbol(Math.round(data.total), currency)}
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: DONUT_SIZE - 50, overflowY: 'auto' }}>
              {data.byBank.map((bank, index) => {
                const share = data.total > 0 ? (bank.value / data.total) * 100 : 0;
                return (
                  <li
                    key={bank.name}
                    style={{ display: 'grid', gridTemplateColumns: '10px minmax(0, 1fr) auto 44px', alignItems: 'center', gap: '10px', padding: '7px 0', borderBottom: '1px solid #f0f2f5', fontSize: '13px' }}
                  >
                    <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: bankColor(index) }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1f2937' }} title={bank.name}>
                      {bank.name}
                    </span>
                    <span style={{ fontWeight: 600, color: '#1f2937', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrencyWithSymbol(Math.round(bank.value), currency)}
                    </span>
                    <span style={{ textAlign: 'right', color: '#767e8e', fontVariantNumeric: 'tabular-nums' }}>
                      {share > 0 && share < 1 ? '<1%' : `${Math.round(share)}%`}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const TOTALS_CONFIG = {
    allocation: { titleKey: 'dashboard.totalAllocation2026', icon: Upload, newRequestPath: '/company/new-request/allocation' },
    hot_money: { titleKey: 'dashboard.totalHotMoney2026', icon: Download, newRequestPath: '/company/new-request/hot-money' },
    vat_credit: { titleKey: 'dashboard.totalVatCredit2026', icon: Receipt, newRequestPath: '/company/vat-credit/new-request' }
  };

  const renderTotalsCard = (productKey) => {
    const productTotals = summary?.totals?.[productKey];
    if (!productTotals) return null;
    const cardKey = `totals-${productKey}`;
    const currencies = sortCurrencies(Object.keys(productTotals.byCurrency));
    const currency = resolveCardCurrency(cardKey, currencies);
    const data = productTotals.byCurrency[currency] || { totalAllocated: 0, lastRate: null };
    const { color } = PRODUCT_STYLES[productKey];
    const { titleKey, icon: Icon, newRequestPath } = TOTALS_CONFIG[productKey];

    return (
      <div className="card" key={productKey} style={{ marginBottom: 0, display: 'flex', flexDirection: 'column' }}>
        <div className="card-title">
          <Icon className="card-title-icon" size={20} style={{ color }} />
          <span style={{ color }}>{t(titleKey)}</span>
          {renderCardCurrencySelector(cardKey, currencies, currency)}
        </div>

        <div style={kpiLabelStyle}>{t('dashboard.totalAllocated')}</div>
        <div style={{ fontSize: '28px', fontWeight: 'bold', color, marginBottom: '16px' }}>
          {formatCurrencyWithSymbol(Math.round(data.totalAllocated), currency)}
        </div>

        <div style={{ display: 'flex', gap: '32px', marginBottom: '20px' }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 700 }}>{productTotals.activeRequests}</div>
            <div style={{ fontSize: '12px', color: '#767e8e' }}>{t('dashboard.activeRequestsCount')}</div>
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 700 }}>{formatRate(data.lastRate)}</div>
            <div style={{ fontSize: '12px', color: '#767e8e' }}>{t('dashboard.lastRate')}</div>
          </div>
        </div>

        <div style={{ marginTop: 'auto' }}>
          <Link to={newRequestPath} className="btn btn-primary">
            {t('sidebar.newRequest')}
          </Link>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">{t('common.loadingDashboard')}</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const products = ['allocation', ...(showHotMoney ? ['hot_money'] : [])];
  // Outstanding card only for products with open positions (the summary lists only currencies with positions)
  const outstandingProducts = products.filter(product => Object.keys(summary?.outstanding?.[product] || {}).length > 0);
  const totalsProducts = [...products, ...(showVatCredit ? ['vat_credit'] : [])];
  const gridColumns = (count) => (isMobile ? '1fr' : `repeat(${count}, minmax(0, 1fr))`);

  return (
    <div className="company-dashboard">
      {/* Active Requests Section (all products) */}
      <div className={`card ${isMobile ? 'mobile-card-view' : ''}`}>
        <div className="card-title" style={{ flexWrap: 'wrap' }}>
          <TrendingUp className="card-title-icon" size={20} />
          <span>{t('dashboard.activeRequests')}</span>
          {activeRequestsFilterOptions.length > 2 &&
            renderFilterButtons(activeRequestsFilterOptions, activeRequestsFilter, setActiveRequestsFilter)}
        </div>

        {!isMobile && (
          <table className="requests-list-table">
            <thead>
              <tr>
                <th>{t('requests.requestId')}</th>
                <th>{t('requests.dateCreated')}</th>
                <th>{t('requests.amount')}</th>
                <th>{t('bids.bidsReceived')}</th>
                <th>{t('requests.maturity')}</th>
                {showHotMoney && <th>{t('requests.type')}</th>}
                <th>{t('bids.status')}</th>
                <th aria-label={t('common.action')} />
              </tr>
            </thead>
            <tbody>
              {filteredActiveRequests.length > 0 ? (
                filteredActiveRequests.map(request => (
                  <tr key={`${request.requestKind || 'liquidity'}-${request.id}`}>
                    <td>#{request.requestId}</td>
                    <td>{new Date(request.createdAt).toLocaleDateString()}</td>
                    <td>{formatCurrencyWithSymbol(request.amount, request.currency || 'EUR')}</td>
                    <td>{formatCurrencyWithSymbol(request.totalActiveBidAmount, request.currency || 'EUR')}</td>
                    <td>{request.requestKind === 'vat_credit' ? '-' : formatDuration(request.duration, t)}</td>
                    {showHotMoney && <td>{renderTypeBadge(getProductKey(request))}</td>}
                    <td>
                      <span className={`status status-${request.status}`}>
                        {getStatusTranslation(request.status)}
                      </span>
                    </td>
                    <td>
                      <Link to={getRequestLink(request)} state={fromDashboard} className="view-btn">
                        {t('common.view')}
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={showHotMoney ? 8 : 7}>{t('dashboard.noActiveRequests')}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {isMobile && (
          <div className="mobile-cards-container">
            {filteredActiveRequests.length > 0 ? (
              filteredActiveRequests.map(request => (
                <MobileActiveRequestCard key={`${request.requestKind || 'liquidity'}-${request.id}`} request={request} />
              ))
            ) : (
              <div className="mobile-record-card mobile-empty-card">
                {t('dashboard.noActiveRequests')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Outstanding: where the money is, by bank */}
      {outstandingProducts.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: gridColumns(outstandingProducts.length), gap: '20px', marginBottom: '20px' }}>
          {outstandingProducts.map(renderOutstandingCard)}
        </div>
      )}

      {/* Upcoming Maturities Section (deposits + hot money, all currencies) */}
      <div className={`card ${isMobile ? 'mobile-card-view' : ''}`}>
        <div className="card-title" style={{ flexWrap: 'wrap' }}>
          <CalendarClock className="card-title-icon" size={20} />
          <span>{t('dashboard.upcomingMaturities')}</span>
          {maturitiesFilterOptions.length > 2 &&
            renderFilterButtons(maturitiesFilterOptions, maturitiesFilter, setMaturitiesFilter)}
        </div>

        {!isMobile && (
          <table className="requests-list-table">
            <thead>
              <tr>
                <th>{t('dashboard.maturityDate')}</th>
                <th>{t('dashboard.daysLeft')}</th>
                {showHotMoney && <th>{t('requests.type')}</th>}
                <th>{t('dashboard.bank')}</th>
                <th>{t('requests.amount')}</th>
                <th>{t('dashboard.rate')}</th>
                <th aria-label={t('common.action')} />
              </tr>
            </thead>
            <tbody>
              {filteredMaturities.length > 0 ? (
                filteredMaturities.map((maturity, index) => (
                  <tr key={`${maturity.requestMongoId}-${maturity.bankName}-${index}`}>
                    <td><strong>{new Date(maturity.endDate).toLocaleDateString()}</strong></td>
                    <td>{formatDaysLeft(maturity.daysLeft)}</td>
                    {showHotMoney && <td>{renderTypeBadge(maturity.requestType)}</td>}
                    <td>{maturity.bankName}</td>
                    <td>{formatCurrencyWithSymbol(maturity.amount, maturity.currency)}</td>
                    <td>{formatRate(maturity)}</td>
                    <td>
                      <Link to={`/company/requests/${maturity.requestMongoId}`} state={fromDashboard} className="view-btn">
                        {t('dashboard.detail')}
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={showHotMoney ? 7 : 6}>
                    {summaryLoading ? t('common.loading') : t('dashboard.noMaturities')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {isMobile && (
          <div className="mobile-cards-container">
            {filteredMaturities.length > 0 ? (
              filteredMaturities.map((maturity, index) => (
                <MobileMaturityCard key={`${maturity.requestMongoId}-${maturity.bankName}-${index}`} maturity={maturity} />
              ))
            ) : (
              <div className="mobile-record-card mobile-empty-card">
                {summaryLoading ? t('common.loading') : t('dashboard.noMaturities')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Allocated totals 2026 per product */}
      <div style={{ display: 'grid', gridTemplateColumns: gridColumns(totalsProducts.length), gap: '20px', marginBottom: '20px' }}>
        {totalsProducts.map(renderTotalsCard)}
      </div>

      {/* Support Contact Section */}
      <div className="card" style={{ textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
          <Mail size={16} style={{ color: '#6c757d' }} />
          <span style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500' }}>
            {t('dashboard.needHelp', { defaultValue: 'Hai bisogno di aiuto?' })}
          </span>
        </div>
        <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>
          {t('dashboard.contactSupport', { defaultValue: 'Contatta il nostro supporto:' })}{' '}
          <a href="mailto:support@euracle.it" style={{ color: '#1a73e8', textDecoration: 'none' }}>
            support@euracle.it
          </a>
        </p>
      </div>
    </div>
  );
};

export default CompanyDashboard;
